/**
 * Where to draw a Silvus mesh: nodes on a ring around the local radio, and one
 * edge per pair of radios however many directions reported it.
 *
 * Silvus reports each direction of a link separately, and the two can differ by
 * ten decibels on an obstructed path. An edge is coloured by the worse of the
 * two, because a link is only as usable as its weaker direction: commands go
 * one way and acknowledgements come back the other.
 */

import type { LinkQuality, SilvusLink, SilvusNode, SilvusReading } from '@/api/mesh';

export interface PlacedNode extends SilvusNode {
  x: number;
  y: number;
}

export interface Edge {
  a: string;
  b: string;
  snr: number;
  quality: LinkQuality;
}

export const QUALITY_RANK: Record<LinkQuality, number> = { poor: 0, fair: 1, good: 2 };

export function placeNodes(nodes: SilvusNode[], size: number): PlacedNode[] {
  const centre = size / 2;
  const radius = size * 0.36;
  const local = nodes.find((node) => node.local);
  const ring = nodes.filter((node) => node !== local);
  const placed: PlacedNode[] = [];
  if (local !== undefined) placed.push({ ...local, x: centre, y: centre });
  ring.forEach((node, index) => {
    // Start at the top and go clockwise, so the order matches the list below.
    const angle = -Math.PI / 2 + (2 * Math.PI * index) / Math.max(1, ring.length);
    placed.push({
      ...node,
      x: centre + radius * Math.cos(angle),
      y: centre + radius * Math.sin(angle),
    });
  });
  return placed;
}

export function edges(links: SilvusLink[]): Edge[] {
  const byPair = new Map<string, Edge>();
  for (const link of links) {
    const [a, b] = [link.source, link.target].sort();
    const key = `${a}|${b}`;
    const existing = byPair.get(key);
    if (existing === undefined || link.snr < existing.snr) {
      byPair.set(key, { a: a!, b: b!, snr: link.snr, quality: link.quality });
    }
  }
  return [...byPair.values()];
}

/** The nodes, including any a link names that the label list left out. */
export function allNodes(reading: SilvusReading): SilvusNode[] {
  const known = new Map(reading.nodes.map((node) => [node.nodeId, node]));
  for (const link of reading.links) {
    if (!known.has(link.source)) {
      known.set(link.source, { nodeId: link.source, label: link.sourceLabel, local: false });
    }
    if (!known.has(link.target)) {
      known.set(link.target, { nodeId: link.target, label: link.targetLabel, local: false });
    }
  }
  return [...known.values()];
}
